/**
 * Minimal MIME header extraction from a raw .eml file (ARCHITECTURE.md §7,
 * "Why not SQLite": a lightweight per-folder index, not full parsing).
 *
 * Reads back only what _index.json needs — Subject, From, Date — from the
 * headers already sitting in the .eml on disk, the same idempotent-render-
 * from-disk pattern Teams uses for index.html. Deliberately not a full MIME
 * parser: no body decoding, no attachment enumeration, no multipart boundary
 * walking. If something else ever needs real MIME parsing, this file should
 * not be pressed into becoming that library.
 */

export interface ParsedHeaders {
    subject?: string;
    from?: string;
    date?: string;
}

/** Header section only — up to the first blank line. Handles both \r\n and \n. */
function headerSection(raw: string): string {
    const match = /\r?\n\r?\n/.exec(raw);
    return match ? raw.slice(0, match.index) : raw;
}

/**
 * Unfolds RFC 5322 folded headers — a continuation line begins with
 * whitespace and belongs to the header above it, not a new header.
 */
function unfoldLines(section: string): string[] {
    const rawLines = section.split(/\r\n|\n/);
    const lines: string[] = [];
    for (const line of rawLines) {
        if (/^[ \t]/.test(line) && lines.length > 0) {
            lines[lines.length - 1] += ` ${line.trim()}`;
        } else if (line.length > 0) {
            lines.push(line);
        }
    }
    return lines;
}

function parseHeaderLines(lines: readonly string[]): Map<string, string> {
    const headers = new Map<string, string>();
    for (const line of lines) {
        const colon = line.indexOf(":");
        if (colon === -1) {
            continue;
        }
        const name = line.slice(0, colon).trim().toLowerCase();
        const value = line.slice(colon + 1).trim();
        // First occurrence wins. A well-formed message has one Subject/Date;
        // a duplicate is malformed input, not a reason to overwrite silently.
        if (!headers.has(name)) {
            headers.set(name, value);
        }
    }
    return headers;
}

const ENCODED_WORD = /=\?([^?]+)\?([BbQq])\?([^?]*)\?=/g;

function decodeQuotedPrintableWord(text: string): string {
    return text.replace(/_/g, " ").replace(/=([0-9A-Fa-f]{2})/g, (_, hex: string) =>
        String.fromCharCode(parseInt(hex, 16))
    );
}

function bytesToUtf8(binaryString: string): string {
    const bytes = Uint8Array.from(binaryString, (c) => c.charCodeAt(0));
    return new TextDecoder("utf-8").decode(bytes);
}

/**
 * Decodes RFC 2047 encoded-words ("=?UTF-8?B?...?=" / "=?UTF-8?Q?...?="),
 * UTF-8 only — the overwhelming majority of modern mail. A legacy charset is
 * left exactly as found rather than mis-decoded as something it is not:
 * garbled text would be worse than the recognisable, still-searchable raw
 * encoded form. Malformed payloads (bad base64, truncated escapes) fall back
 * the same way rather than throwing.
 */
export function decodeEncodedWords(text: string): string {
    return text.replace(ENCODED_WORD, (whole, charset: string, enc: string, payload: string) => {
        if (charset.toUpperCase() !== "UTF-8") {
            return whole;
        }
        try {
            const binary = enc.toUpperCase() === "B" ? atob(payload) : decodeQuotedPrintableWord(payload);
            return bytesToUtf8(binary);
        } catch {
            return whole;
        }
    });
}

/** Extracts Subject/From/Date from a raw .eml's headers. Never throws. */
export function parseHeaders(raw: string): ParsedHeaders {
    const headers = parseHeaderLines(unfoldLines(headerSection(raw)));

    const subject = headers.get("subject");
    const from = headers.get("from");
    const date = headers.get("date");

    return {
        ...(subject !== undefined ? { subject: decodeEncodedWords(subject) } : {}),
        ...(from !== undefined ? { from: decodeEncodedWords(from) } : {}),
        ...(date !== undefined ? { date } : {})
    };
}
